import { memo } from "react"
import { Grid, Theme, MenuItem, TextField } from "@mui/material"

import { makeStyles } from "makeStyles"

import MeasureCylinderDialog from "./subComponents/MeasureCylinderDialog"
import LockingMechanismConfiguration from "./subComponents/LockingMechanismConfiguration"

import useInnerLengthsByOuterLengthsConfiguration from "@secureo/common/hooks/cylinderConfigurations/useInnerLengthsByOuterLengthsConfiguration"
import useOuterLengthsConfiguration from "@secureo/common/hooks/cylinderConfigurations/useOuterLengthsConfiguration"

import {
	DispatchProductDetailPageReducer,
	setSelectedOuterLength,
	setSelectedInnerLength,
	ProductDetailPageReducerState,
} from "@secureo/common/reducers/productDetailPageReducer"
import { InnerLengthsByOuterLengths } from "@secureo/common/typings/ProductAttributes"

const useStyles = makeStyles()((theme: Theme) => ({
	formControl: {
		marginBottom: theme.spacing(1),
	},
}))

interface InnerAndOuterLengthConfigurationInputProps {
	innerLengthsByOuterLengths: InnerLengthsByOuterLengths
	selectedOuterLength: number
	selectedInnerLength: number
	onOuterLengthChange: (selectedOuterLength: number) => void
	onInnerLengthChange: (selectedInnerLength: number) => void
	disabled?: boolean
}

const InnerAndOuterLengthConfigurationInput = ({
	innerLengthsByOuterLengths,
	selectedOuterLength,
	selectedInnerLength,
	onOuterLengthChange,
	onInnerLengthChange,
	disabled,
}: InnerAndOuterLengthConfigurationInputProps) => {
	const { classes } = useStyles()
	const {
		availableOuterLengths,
		availableInnerLengths,
		outerLengthInputProps,
		innerLengthInputProps,
	} = useInnerLengthsByOuterLengthsConfiguration(
		innerLengthsByOuterLengths,
		selectedOuterLength,
		selectedInnerLength,
		onOuterLengthChange,
		onInnerLengthChange,
	)

	return (
		<>
			<Grid item xs={6} sm={5} md={4}>
				<TextField
					id="outerLength"
					className={classes.formControl}
					label="Aussenlänge"
					{...outerLengthInputProps}
					margin="normal"
					variant="outlined"
					select
					fullWidth
					disabled={disabled}
				>
					{availableOuterLengths.map((outerLength) => (
						<MenuItem key={outerLength} value={outerLength}>
							{outerLength} mm
						</MenuItem>
					))}
				</TextField>
			</Grid>
			<Grid item xs={6} sm={5} md={4}>
				<TextField
					id="innerLength"
					className={classes.formControl}
					label="Innenlänge"
					{...innerLengthInputProps}
					margin="normal"
					variant="outlined"
					select
					fullWidth
					disabled={disabled}
				>
					{availableInnerLengths.map((innerLength) => (
						<MenuItem key={innerLength} value={innerLength}>
							{innerLength} mm
						</MenuItem>
					))}
				</TextField>
			</Grid>
		</>
	)
}

interface OuterLengthConfigurationInputProps {
	outerLengths: number[]
	selectedOuterLength: number
	onOuterLengthChange: (selectedOuterLength: number) => void
	disabled?: boolean
}

const OuterLengthConfigurationInput = ({
	outerLengths,
	selectedOuterLength,
	onOuterLengthChange,
	disabled,
}: OuterLengthConfigurationInputProps) => {
	const { classes } = useStyles()
	const { outerLengthInputProps, availableOuterLengths } = useOuterLengthsConfiguration(
		outerLengths,
		selectedOuterLength,
		onOuterLengthChange,
	)

	return (
		<Grid item xs={6} sm={5} md={4}>
			<TextField
				id="outerLength"
				className={classes.formControl}
				label="Aussenlänge"
				{...outerLengthInputProps}
				margin="normal"
				variant="outlined"
				select
				fullWidth
				disabled={disabled}
			>
				{availableOuterLengths.map((outerLength) => (
					<MenuItem key={outerLength} value={outerLength}>
						{outerLength} mm
					</MenuItem>
				))}
			</TextField>
		</Grid>
	)
}

const InnerAndOuterLength = ({ accessToken, reducerState, dispatch }: Props) => {
	const {
		isLoading,
		selectedOuterLength,
		selectedInnerLength,
		cylinderLockingMechanismProducts,
		cylinderConfigurationType,
		attributes: { innerLengthsByOuterLengths, outerLengths },
	} = reducerState

	const onOuterLengthChange = (selectedOuterLength: number) => {
		setSelectedOuterLength(accessToken, dispatch, reducerState, selectedOuterLength)
	}

	const onInnerLengthChange = (selectedInnerLength: number) => {
		setSelectedInnerLength(accessToken, dispatch, reducerState, selectedInnerLength)
	}

	return (
		<>
			<Grid container spacing={3}>
				{cylinderConfigurationType === "innerLengthsByOuterLengths" && (
					<InnerAndOuterLengthConfigurationInput
						innerLengthsByOuterLengths={innerLengthsByOuterLengths}
						selectedOuterLength={selectedOuterLength}
						selectedInnerLength={selectedInnerLength}
						onOuterLengthChange={onOuterLengthChange}
						onInnerLengthChange={onInnerLengthChange}
						disabled={isLoading || !accessToken}
					/>
				)}
				{cylinderConfigurationType === "outerLengths" && (
					<OuterLengthConfigurationInput
						outerLengths={outerLengths}
						selectedOuterLength={selectedOuterLength}
						onOuterLengthChange={onOuterLengthChange}
						disabled={isLoading || !accessToken}
					/>
				)}
			</Grid>

			{cylinderLockingMechanismProducts && (
				<LockingMechanismConfiguration dispatch={dispatch} reducerState={reducerState} />
			)}
			<MeasureCylinderDialog />
		</>
	)
}

interface Props {
	accessToken: string
	dispatch: DispatchProductDetailPageReducer
	reducerState: ProductDetailPageReducerState
}

export default memo(InnerAndOuterLength)
